import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ElementRef,
  HostListener
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';

import { LanguageService } from '../../services/language.service';
import { BnNumberPipe } from '../../pipes/bn-number.pipe';

// ফিল্টার সাইডবার একজন ইউজার যা যা সিলেক্ট করেছে, তার state
export interface ProductFilters {
  brands: string[];
  weights: string[];
  minPrice: number | null;
  maxPrice: number | null;
  inStock: boolean;
}

interface BrandCount { brand: string; count: number; }
interface WeightCount { weight: string; count: number; }
interface CategoryFacets {
  brands: BrandCount[];
  weights: WeightCount[];
  minPrice: number | null;
  maxPrice: number | null;
}

// =========================================================
// 🔎 FILTER SIDEBAR — ক্যাটাগরি পেজে Rokomari-স্টাইল ফিল্টার সাইডবার।
// Brand / Weight — accordion-এ কোলাপ্সিবল checkbox লিস্ট (ভেতরে
// সার্চ বক্স, ব্র্যান্ড লিস্ট বড় হলে খুঁজে নেওয়া সহজ), Price Range —
// Min/Max ইনপুট, In Stock — একটা টগল checkbox।
// এই কম্পোনেন্ট নিজে থেকে ফিল্টারও প্রয়োগ করে না, প্রোডাক্ট লিস্টও আনে
// না — শুধু ইউজারের সিলেকশন (filtersChanged) প্যারেন্ট পেজে পাঠায়;
// প্যারেন্ট পেজ (category-page) সেটা দিয়ে প্রোডাক্ট রিফেচ করে।
// =========================================================
@Component({
  selector: 'app-filter-sidebar',
  standalone: true,
  imports: [CommonModule, FormsModule, BnNumberPipe],
  templateUrl: './filter-sidebar.html',
  styleUrls: ['./filter-sidebar.css']
})
export class FilterSidebar implements OnInit, OnChanges {

  @Input() categoryId!: number;

  // সার্চ মোড: প্রোডাক্ট লিস্ট দিলে ব্র্যান্ড/ওজন/দাম সেই লিস্ট থেকেই বের হয়,
  // সার্ভারে কিছু চায় না (ক্যাটাগরি পেজে এটা দেওয়া হয় না)
  @Input() products: any[] | null = null;
  @Output() filtersChanged = new EventEmitter<ProductFilters>();

  apiUrl = 'https://superbangladesh-api-1.onrender.com';

  facets: CategoryFacets = { brands: [], weights: [], minPrice: null, maxPrice: null };
  loading = false;

  // sections open/closed — Rokomari-র মতোই Brand/Weight ডিফল্ট খোলা,
  // বাকিগুলো বন্ধ থাকবে যতক্ষণ না ইউজার ক্লিক করে
  // হরাইজন্টাল বারে ড্রপডাউন — শুরুতে সব বন্ধ, একসাথে শুধু একটা খোলা থাকে
  sectionsOpen: { [key: string]: boolean } = {
    brand: false,
    weight: false,
    price: false
  };

  brandSearch = '';

  selected: ProductFilters = {
    brands: [],
    weights: [],
    minPrice: null,
    maxPrice: null,
    inStock: false
  };

  constructor(
    private http: HttpClient,
    public languageService: LanguageService,
    private host: ElementRef<HTMLElement>
  ) {}

  // বারের বাইরে ক্লিক করলে খোলা ড্রপডাউন বন্ধ হবে
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const path = (event.composedPath && event.composedPath()) || [];
    if (!path.includes(this.host.nativeElement)) {
      this.closeAll();
    }
  }

  closeAll(): void {
    Object.keys(this.sectionsOpen).forEach(k => this.sectionsOpen[k] = false);
  }

  ngOnInit(): void {
    this.loadFacets();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['products'] && this.products) {
      this.selected = { brands: [], weights: [], minPrice: null, maxPrice: null, inStock: false };
      this.brandSearch = '';
      this.closeAll();
      this.facets = this.buildFacets(this.products);
      return;
    }
    if (changes['categoryId'] && !changes['categoryId'].isFirstChange()) {
      this.resetSelection();
      this.loadFacets();
    }
  }

  t(key: string): string {
    return this.languageService.translate(key);
  }

  bn(): boolean {
    return this.languageService.isBangla();
  }

  // ক্যাটাগরি অনুযায়ী ফিল্টার অপশন মেমরিতে রাখা — আবার ঢুকলে সাথে সাথে দেখায়
  private static facetCache = new Map<number, CategoryFacets>();

  loadFacets(): void {
    if (!this.categoryId) return;

    const id = Number(this.categoryId);
    const cached = FilterSidebar.facetCache.get(id);

    if (cached) {
      this.facets = cached;
      this.loading = false;
    } else {
      this.loading = true;
    }

    this.http
      .get<CategoryFacets>(`${this.apiUrl}/api/products/category/${id}/filters`)
      .subscribe({
        next: (res) => {
          // পুরনো ক্যাটাগরির রেসপন্স এসে নতুনটাকে ওভাররাইট করবে না
          if (Number(this.categoryId) !== id) return;

          this.facets = res || { brands: [], weights: [], minPrice: null, maxPrice: null };
          FilterSidebar.facetCache.set(id, this.facets);
          this.loading = false;
        },
        error: () => {
          if (Number(this.categoryId) !== id) return;

          if (!cached) {
            this.facets = { brands: [], weights: [], minPrice: null, maxPrice: null };
          }
          this.loading = false;
        }
      });
  }

  private buildFacets(list: any[]): CategoryFacets {
    const brandMap = new Map<string, number>();
    const weightMap = new Map<string, number>();
    let min: number | null = null;
    let max: number | null = null;

    for (const p of list) {
      const b = (p?.brand ?? '').toString().trim();
      if (b) brandMap.set(b, (brandMap.get(b) || 0) + 1);

      const w = (p?.weight ?? '').toString().trim();
      if (w) weightMap.set(w, (weightMap.get(w) || 0) + 1);

      const price = Number(p?.price);
      if (!isNaN(price) && p?.price !== null && p?.price !== undefined) {
        min = min === null ? price : Math.min(min, price);
        max = max === null ? price : Math.max(max, price);
      }
    }

    return {
      brands: [...brandMap.entries()].map(([brand, count]) => ({ brand, count })).sort((a, b) => a.brand.localeCompare(b.brand)),
      weights: [...weightMap.entries()].map(([weight, count]) => ({ weight, count })).sort((a, b) => a.weight.localeCompare(b.weight)),
      minPrice: min,
      maxPrice: max
    };
  }

  get filteredBrandList(): BrandCount[] {
    const q = this.brandSearch.trim().toLowerCase();
    if (!q) return this.facets.brands;
    return this.facets.brands.filter(b => b.brand.toLowerCase().includes(q));
  }

  toggleSection(key: string): void {
    const willOpen = !this.sectionsOpen[key];
    this.closeAll();
    this.sectionsOpen[key] = willOpen;
  }

  toggleBrand(brand: string): void {
    const i = this.selected.brands.indexOf(brand);
    if (i > -1) this.selected.brands.splice(i, 1);
    else this.selected.brands.push(brand);
    this.emitChange();
  }

  isBrandSelected(brand: string): boolean {
    return this.selected.brands.includes(brand);
  }

  toggleWeight(weight: string): void {
    const i = this.selected.weights.indexOf(weight);
    if (i > -1) this.selected.weights.splice(i, 1);
    else this.selected.weights.push(weight);
    this.emitChange();
  }

  isWeightSelected(weight: string): boolean {
    return this.selected.weights.includes(weight);
  }

  toggleInStock(): void {
    this.selected.inStock = !this.selected.inStock;
    this.emitChange();
  }

  onPriceChange(): void {
    this.emitChange();
  }

  resetSelection(): void {
    this.selected = { brands: [], weights: [], minPrice: null, maxPrice: null, inStock: false };
    this.brandSearch = '';
    this.emitChange();
  }

  hasActiveFilters(): boolean {
    return this.selected.brands.length > 0
      || this.selected.weights.length > 0
      || this.selected.minPrice !== null
      || this.selected.maxPrice !== null
      || this.selected.inStock;
  }

  private emitChange(): void {
    this.filtersChanged.emit({ ...this.selected });
  }
}
